import * as React from 'react';
import Helmet from 'react-helmet';

type AuthorsList = { name: string }[];

interface GoogleNewsSchemaDataProps {
  canonical: string;
  title: string;
  image: string;
  datePublished: string;
  authors: AuthorsList;
  publisherName: string;
  logoUrl: string;
  dateModified: string;
}

const dateToIsoDate = (date: string) => {
  return (new Date(date)).toISOString();
};

const getAuthorsListSchema = (authors: AuthorsList) => {
  if (authors.length === 1) {
    return {
      '@type': 'Person',
      name: authors[0].name,
    };
  }

  return authors.map(author => ({
    '@type': 'Person',
    name: author.name,
  }));
};

const getSchemaData = (
  canonical: string,
  title: string,
  image: string,
  datePublished: string,
  authors: AuthorsList,
  publisherName: string,
  logoUrl: string,
  dateModified: string,
) => {
  return {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    headline: title,
    image,
    datePublished: dateToIsoDate(datePublished),
    dateModified: dateToIsoDate(dateModified),
    author: getAuthorsListSchema(authors),
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
  };
};

export const SchemaScript: React.FunctionComponent<GoogleNewsSchemaDataProps> = props => {
  const { canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified } = props;
  const schema = getSchemaData(canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified);
  return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
};

export const GoogleNewsSchema: React.FunctionComponent<GoogleNewsSchemaDataProps> = props => {
  const { canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified } = props;
  const schema = getSchemaData(canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified);
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
